import { insertNewRow } from "../insertRows";
import type { Transaction } from "@rocicorp/zero";
import * as z from "zod";
import { effortLevels } from "../aiProviders";
import { userFacingError } from "../authorization";
import { ensureWritable } from "../copyOnWrite";
import { zql } from "../generatedDrizzleSchema";
import { actorColumns } from "../rbac/roles";
import { stripNullBytes } from "../stripNullBytes";
import {
  assertChatAttachmentsAllowed,
  attachmentSchema,
} from "./attachmentValidation";
import { mutator } from "./mutator";
import { clientGeneratableIdSchema } from "../id";
import {
  branchSchema,
  insertUserInterruptEntry,
  nextRespondingAt,
} from "./utils";

export const sendMessageSchema = z
  .object({
    entryId: clientGeneratableIdSchema,
    expectedHeadCommitId: z.string().optional(),
    text: z.string().transform(stripNullBytes),
    isDictated: z.boolean().optional(),
    planMode: z.boolean().optional(),
    effort: z.enum(effortLevels).optional(),
    chatModel: z.string().optional(),
    actionKind: z
      .enum([
        "readme",
        "error",
        "connector",
        "investigate_step_test",
        "investigate_step_status",
        "step_test_fixture_setup",
        "add_tests",
        "sub_agent",
      ])
      .optional(),
    attachments: z.array(attachmentSchema).optional(),
    selectedStep: z.string().optional(),
    interruptEntryId: clientGeneratableIdSchema.optional(),
    clientSentAt: z.number().int().optional(),
    clientTimezone: z.string().optional(),
  })
  .merge(branchSchema);

export const sendMessage = mutator({
  redactKeysOnSecret: ["text"],
  schema: sendMessageSchema,
  resolve: {
    branch: { kind: "branch", from: "branchId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.branch.workflow.spaceId,
    },
  ],
  fn: async ({ tx, ctx, args }) => {
    const {
      entryId,
      text,
      isDictated,
      planMode,
      effort,
      actionKind,
      attachments,
      interruptEntryId,
      branch,
      forkBranchId,
      forkCommitId,
      forkChatId,
      workingCommitId,
      workingChatId,
    } = args;
    if (!text.trim() && (!attachments || attachments.length === 0)) {
      throw userFacingError("Message must have text or attachments");
    }
    assertChatAttachmentsAllowed(attachments);
    const actor = actorColumns(ctx);

    const writable = await ensureWritable(
      tx,
      ctx,
      ctx.tenantId,
      {
        branchId: branch.id,
        expectedHeadCommitId: args.expectedHeadCommitId,
        forkBranchId,
        forkCommitId,
        forkChatId,
        workingCommitId,
        workingChatId,
      },
      branch
    );

    const [chat] = await tx.run(zql.chats.where("commitId", writable.commitId));
    if (!chat) throw userFacingError("Chat not found");

    const createdAt = Date.now();

    let previousEntryId = await lastEntryId(tx, chat.id);
    // A fresh working chat continues its parent checkpoint’s conversation, so a
    // turn still running there sees this message as its successor and stops.
    if (previousEntryId === null && writable.branchId === branch.id) {
      const [commit] = await tx.run(
        zql.workflowCommits.where("id", writable.commitId)
      );
      if (commit?.parentCommitId) {
        const [parentChat] = await tx.run(
          zql.chats.where("commitId", commit.parentCommitId)
        );
        if (parentChat) previousEntryId = await lastEntryId(tx, parentChat.id);
      }
    }

    if (interruptEntryId) {
      await insertUserInterruptEntry(tx, {
        id: interruptEntryId,
        chatId: chat.id,
        createdAt,
        previousEntryId,
        tenantId: ctx.tenantId,
      });
    }

    await insertNewRow(tx, "chatEntries", {
      id: entryId,
      chatId: chat.id,
      entryType: "UserMessage",
      createdAt: interruptEntryId ? createdAt + 1 : createdAt,
      previousEntryId: interruptEntryId ?? previousEntryId,
      tenantId: ctx.tenantId,
    });
    await tx.mutate.userMessages.insert({
      entryId,
      userId: actor.userId,
      serviceAccountId: actor.serviceAccountId,
      text,
      isDictated: isDictated ?? null,
      planMode: planMode ?? null,
      effort: effort ?? null,
      actionKind: actionKind ?? null,
      tenantId: ctx.tenantId,
    });

    const [wrBranch] = await tx.run(
      zql.workflowBranches.where("id", writable.branchId)
    );
    if (!wrBranch) throw userFacingError("Branch not found");
    const [workflow] = await tx.run(
      zql.workflows.where("id", wrBranch.workflowId)
    );
    if (workflow) {
      await tx.mutate.spaces.update({
        id: workflow.spaceId,
        updatedAt: createdAt,
      });
    }
    for (const att of attachments ?? []) {
      await insertNewRow(tx, "attachments", {
        id: att.id,
        entryId,
        fileName: att.fileName,
        mediaType: att.mediaType,
        sizeBytes: att.sizeBytes,
        tenantId: ctx.tenantId,
      });
    }

    // Contributors are the human facepile; a pure service account records none.
    if (actor.userId) {
      await tx.mutate.branchContributors.upsert({
        branchId: writable.branchId,
        workflowId: wrBranch.workflowId,
        userId: actor.userId,
        lastMessagedAt: createdAt,
        tenantId: ctx.tenantId,
      });
    }

    await tx.mutate.workflowBranches.update({
      id: writable.branchId,
      lastChatEntryAt: createdAt,
    });

    await tx.mutate.chats.update({
      id: chat.id,
      lastChatEntryType: "UserMessage",
      respondingAt: nextRespondingAt(chat.respondingAt),
      awaitingAction: false,
    });

    await tx.mutate.workflows.update({
      id: wrBranch.workflowId,
      updatedAt: createdAt,
    });
  },
});

async function lastEntryId(
  tx: Transaction,
  chatId: string
): Promise<string | null> {
  const [last] = await tx.run(
    zql.chatEntries
      .where("chatId", chatId)
      .orderBy("createdAt", "desc")
      .limit(1)
  );
  return last?.id ?? null;
}
